// Meeting transcript of one clip: segments grouped by consecutive speaker, editable in place,
// timestamps seek the player, the segment being played is highlighted and kept in view.
import { memo, useEffect, useRef } from 'react';
import type { Segment, Speaker } from '../../../shared/types';
import type { ViewClip } from '../lib/api';
import { defaultSpeakerName, formatOffset, speakerColor } from '../lib/format';
import { EditableParagraph, EditableText } from './EditableText';
import { prefersReducedMotion } from './helpers';
import { Menu, type MenuItem } from './Menu';

export interface SegmentGroup {
  speaker: string | null;
  segments: Segment[];
}

/** Consecutive segments of the same speaker form one group (one "turn"). */
export function groupSegments(segments: readonly Segment[]): SegmentGroup[] {
  const groups: SegmentGroup[] = [];
  for (const segment of segments) {
    const last = groups[groups.length - 1];
    if (last && last.speaker === segment.speaker) last.segments.push(segment);
    else groups.push({ speaker: segment.speaker, segments: [segment] });
  }
  return groups;
}

/** Segment that is playing at `ms`: the last one that started at or before it. */
export function segmentAt(segments: readonly Segment[], ms: number): Segment | null {
  let found: Segment | null = null;
  for (const segment of segments) {
    if (segment.startMs <= ms) found = segment;
    else break;
  }
  if (found && found.endMs > found.startMs && ms > found.endMs + 1_500) {
    // In a long pause after the segment: nothing is being said.
    return null;
  }
  return found;
}

// ---------- "is the user scrolling?" (auto-scroll must never fight the user) ----------

let lastUserScroll = 0;
let scrollTrackingInstalled = false;

function installScrollTracking(): void {
  if (scrollTrackingInstalled || typeof window === 'undefined') return;
  scrollTrackingInstalled = true;
  const mark = () => {
    lastUserScroll = Date.now();
  };
  window.addEventListener('wheel', mark, { passive: true });
  window.addEventListener('touchmove', mark, { passive: true });
  window.addEventListener('keydown', (e) => {
    if (['PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End', ' '].includes(e.key)) mark();
  });
}

const USER_SCROLL_QUIET_MS = 4_000;

function keepInView(el: HTMLElement): void {
  if (Date.now() - lastUserScroll < USER_SCROLL_QUIET_MS) return;
  // Never move the page while the user is editing text.
  const active = document.activeElement;
  if (active instanceof HTMLElement && (active.isContentEditable || active.tagName === 'TEXTAREA' || active.tagName === 'INPUT')) return;
  const rect = el.getBoundingClientRect();
  const bottomBar = document.querySelector('.player')?.getBoundingClientRect().height ?? 0;
  const topBar = document.querySelector('.topbar')?.getBoundingClientRect().height ?? 0;
  if (rect.top >= topBar + 8 && rect.bottom <= window.innerHeight - bottomBar - 8) return;
  el.scrollIntoView?.({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}

// ---------- component ----------

export interface TranscriptHandlers {
  onSeek: (clipId: string, ms: number) => void;
  onSaveSegment: (clipId: string, segmentId: string, text: string) => void;
  /** speakerKey null = create a new speaker. */
  onReassign: (clipId: string, segmentIds: string[], speakerKey: string | null) => void;
  onSaveClipText: (clipId: string, text: string) => void;
}

interface ClipTranscriptProps extends TranscriptHandlers {
  clip: ViewClip;
  speakers: Speaker[];
  activeSegmentId: string | null;
}

function nameOf(speakers: Speaker[], key: string | null): string {
  if (!key) return 'Neznan govorec';
  return speakers.find((s) => s.key === key)?.name || defaultSpeakerName(key);
}

export const ClipTranscript = memo(function ClipTranscript(props: ClipTranscriptProps) {
  const { clip, speakers, activeSegmentId, onSeek, onSaveSegment, onReassign, onSaveClipText } = props;
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(installScrollTracking, []);

  useEffect(() => {
    if (!activeSegmentId || !rootRef.current) return;
    const nodes = rootRef.current.querySelectorAll<HTMLElement>('[data-segment-id]');
    for (const el of nodes) {
      if (el.dataset.segmentId === activeSegmentId) {
        keepInView(el);
        break;
      }
    }
  }, [activeSegmentId]);

  const diarized = clip.diarized && clip.segments.length > 0 && clip.segments.some((s) => s.speaker !== null);

  if (!diarized) {
    if (!clip.text.trim() && clip.status === 'done') {
      return <p className="muted transcript__empty">V posnetku ni bilo prepoznanega govora.</p>;
    }
    const first = clip.segments[0];
    return (
      <div className="transcript transcript--plain" ref={rootRef}>
        {clip.hasAudio && (
          <button type="button" className="ts-btn" onClick={() => onSeek(clip.id, first?.startMs ?? 0)} aria-label="Predvajaj od začetka">
            {formatOffset(first?.startMs ?? 0)}
          </button>
        )}
        <EditableText value={clip.text} onSave={(text) => onSaveClipText(clip.id, text)} ariaLabel="Besedilo prepisa" className="transcript__plain-text" />
      </div>
    );
  }

  const groups = groupSegments(clip.segments);
  return (
    <div className="transcript" ref={rootRef}>
      {groups.map((group) => {
        const first = group.segments[0]!;
        const name = nameOf(speakers, group.speaker);
        /** Menu items that move the group's segments from `from` on to another speaker. */
        const reassignItems = (from: number): MenuItem[] => {
          const ids = () => group.segments.slice(from).map((seg) => seg.id);
          const items: MenuItem[] = speakers
            .filter((s) => s.key !== group.speaker)
            .map((s) => ({
              label: s.name || defaultSpeakerName(s.key),
              icon: 'user' as const,
              onSelect: () => onReassign(clip.id, ids(), s.key),
            }));
          items.push({ label: 'Nov govorec', icon: 'plus', separator: items.length > 0, onSelect: () => onReassign(clip.id, ids(), null) });
          return items;
        };
        const items = reassignItems(0);
        return (
          <div className="turn" key={first.id} style={{ ['--speaker' as string]: speakerColor(group.speaker) }}>
            <div className="turn__head">
              <Menu
                items={items}
                label={`Govorec: ${name}. Pripiši drugemu govorcu`}
                heading={group.segments.length > 1 ? 'Ta odsek pripiši govorcu' : 'Pripiši govorcu'}
                align="start"
                triggerClassName="turn__speaker"
                trigger={
                  <>
                    <span className="speaker-dot" aria-hidden="true" />
                    <span>{name}</span>
                  </>
                }
              />
              <button
                type="button"
                className="ts-btn"
                onClick={() => onSeek(clip.id, first.startMs)}
                aria-label={`Predvajaj od ${formatOffset(first.startMs)}`}
              >
                {formatOffset(first.startMs)}
              </button>
            </div>
            <div className="turn__body">
              {group.segments.map((segment, i) => (
                <div key={segment.id} className="segment-row">
                  {i > 0 && (
                    // A missed speaker change: split the turn here and give the rest to someone else.
                    <Menu
                      items={reassignItems(i)}
                      label={`Odsek ${formatOffset(segment.startMs)}: od tu naprej pripiši drugemu govorcu`}
                      heading="Od tu naprej pripiši govorcu"
                      align="start"
                      triggerClassName="segment-row__speaker"
                      trigger={<span className="speaker-dot" aria-hidden="true" />}
                    />
                  )}
                  <EditableParagraph
                    value={segment.text}
                    ariaLabel={`Besedilo, ${name}, ${formatOffset(segment.startMs)}`}
                    className={`segment${segment.id === activeSegmentId ? ' is-active' : ''}`}
                    onSave={(text) => onSaveSegment(clip.id, segment.id, text)}
                    segmentId={segment.id}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
});
